import React, { useState, useMemo } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const descriptions = {
  "Realistic (R)":
    "Practical, hands-on, physically active; enjoys building, fixing, or working outdoors.",
  "Investigative (I)":
    "Analytical, intellectual, scientific; enjoys research, problem-solving, and understanding how things work.",
  "Artistic (A)":
    "Creative, original, expressive; enjoys art, design, music, writing, and freedom of expression.",
  "Social (S)":
    "Empathetic, collaborative, supportive; enjoys helping others, teaching, and community involvement.",
  "Enterprising (E)":
    "Persuasive, ambitious, risk-taking; enjoys leadership, business ventures, and motivating others.",
  "Conventional (C)":
    "Organized, detail-oriented, structured; enjoys data management, routines, and systematic work.",
};

function CustomPolarAngleAxisTick({
  payload,
  x,
  y,
  setSelectedCategory,
  selectedCategory,
}) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (selectedCategory === payload.value) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(payload.value);
    }
  };

  return (
    <text
      x={x}
      y={y}
      fill={isHovered ? "#333" : "#444"}
      textAnchor="middle"
      dominantBaseline="central"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        cursor: "pointer",
        fontSize: isHovered ? 13 : 12,
        userSelect: "none",
      }}
    >
      {payload.value}
    </text>
  );
}

const RIASECHexagonChartDouble = ({ results, results2 }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Determine if results2 is provided and non-empty.
  const isDual = results2 && Object.keys(results2).length > 0;

  // Single data set.
  const singleData = useMemo(
    () => [
      {
        category: "Realistic (R)",
        score: Math.round((results?.Realistic || 0) * 100),
      },
      {
        category: "Investigative (I)",
        score: Math.round((results?.Investigative || 0) * 100),
      },
      {
        category: "Artistic (A)",
        score: Math.round((results?.Artistic || 0) * 100),
      },
      {
        category: "Social (S)",
        score: Math.round((results?.Social || 0) * 100),
      },
      {
        category: "Enterprising (E)",
        score: Math.round((results?.Enterprising || 0) * 100),
      },
      {
        category: "Conventional (C)",
        score: Math.round((results?.Conventional || 0) * 100),
      },
    ],
    [results]
  );

  // Dual data set.
  const dualData = useMemo(
    () => [
      {
        category: "Realistic (R)",
        program: Math.round((results?.Realistic || 0) * 100),
        you: Math.round((results2?.Realistic || 0) * 100),
      },
      {
        category: "Investigative (I)",
        program: Math.round((results?.Investigative || 0) * 100),
        you: Math.round((results2?.Investigative || 0) * 100),
      },
      {
        category: "Artistic (A)",
        program: Math.round((results?.Artistic || 0) * 100),
        you: Math.round((results2?.Artistic || 0) * 100),
      },
      {
        category: "Social (S)",
        program: Math.round((results?.Social || 0) * 100),
        you: Math.round((results2?.Social || 0) * 100),
      },
      {
        category: "Enterprising (E)",
        program: Math.round((results?.Enterprising || 0) * 100),
        you: Math.round((results2?.Enterprising || 0) * 100),
      },
      {
        category: "Conventional (C)",
        program: Math.round((results?.Conventional || 0) * 100),
        you: Math.round((results2?.Conventional || 0) * 100),
      },
    ],
    [results, results2]
  );

  // Choose the appropriate data set.
  const finalData = isDual ? dualData : singleData;

  // Update the detail box based on the mode.
  const getRelativeRankInfo = (catLabel) => {
    const item = finalData.find((d) => d.category === catLabel);
    if (!item) return "";
    return isDual
      ? `Program: ${item.program}%, You: ${item.you}%`
      : `Score: ${item.score}%`;
  };

  return (
    <div style={{ width: "100%", maxWidth: 600 }}>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <RadarChart
            data={finalData}
            outerRadius="90%"
            margin={{ right: 5, left: 5 }}
          >
            <PolarGrid stroke="#ccc" strokeDasharray="3 3" />
            <PolarAngleAxis
              dataKey="category"
              tick={(props) => (
                <CustomPolarAngleAxisTick
                  {...props}
                  setSelectedCategory={setSelectedCategory}
                  selectedCategory={selectedCategory}
                />
              )}
            />
            <PolarRadiusAxis
              domain={[0, "dataMax + 10"]}
              tick={false}
              axisLine={false}
            />
            <Tooltip formatter={(value) => `${value}%`} />
            {isDual ? (
              <>
                <Radar
                  name="Program"
                  dataKey="program"
                  stroke="#8884d8"
                  fill="#8884d8"
                  fillOpacity={0.6}
                  isAnimationActive
                  animationBegin={200}
                  animationDuration={1500}
                  animationEasing="ease-in-out"
                />
                <Radar
                  name="You"
                  dataKey="you"
                  stroke="#82ca9d"
                  fill="#82ca9d"
                  fillOpacity={0.6}
                  isAnimationActive
                  animationBegin={200}
                  animationDuration={1500}
                  animationEasing="ease-in-out"
                />
              </>
            ) : (
              <Radar
                name="Score"
                dataKey="score"
                stroke="#8884d8"
                fill="#8884d8"
                fillOpacity={0.6}
                isAnimationActive
                animationBegin={200}
                animationDuration={1500}
                animationEasing="ease-in-out"
              />
            )}
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Detail Box */}
      <div
        className={`mt-2 rounded border border-gray-300 bg-gray-50 transition-all duration-300 ease-in-out ${
          selectedCategory ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        style={{ padding: "0.5rem 0.75rem" }}
      >
        {selectedCategory && (
          <>
            <p className="font-semibold mb-1">{selectedCategory}</p>
            <p className="text-sm mb-1">
              {descriptions[selectedCategory] || "No description available."}
            </p>
            <p className="text-sm italic">
              {getRelativeRankInfo(selectedCategory)}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default RIASECHexagonChartDouble;
